/**
 * What support contributes, in two groups mounted separately.
 *
 * `supportRoutes` is the desk's: the queue and one claim. Both read
 * `support.ticket.read`, and what a user can *do* on the claim is decided by
 * the two write permissions, which the screen reads itself — taking a claim
 * and answering it are the desk's daily work, and deciding it is the
 * distributor's answer to the customer.
 *
 * `portalSupportRoutes` is the customer's. No permission on either: a
 * customer's rights are the state of their `client_users` link, which the
 * server resolves on every request.
 *
 * The portal's routes take `clientCode` as a **function**, called during
 * render. The customer context lives in `feature-shop` and a feature may not
 * import a sibling feature, so the composition root — which is allowed to
 * know both exist — passes a reader in. Being called during render is what
 * lets that reader be a hook.
 */

import { useParams } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { PortalTicketScreen } from "./screens/PortalTicketScreen";
import { PortalTicketsScreen } from "./screens/PortalTicketsScreen";
import { TicketScreen } from "./screens/TicketScreen";
import { TicketsScreen } from "./screens/TicketsScreen";
import { SUPPORT_PATHS, SUPPORT_PORTAL_PATHS } from "./paths";
import "./i18n";

export function supportRoutes(client: ApiClient): FeatureRoutes {
  function Tickets() {
    return <TicketsScreen client={client} />;
  }

  function OneTicket() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types. The id is
    // validated by the server on every call it is used in.
    const params: { id?: string } = useParams({ strict: false });
    return <TicketScreen client={client} id={params.id ?? ""} />;
  }

  return {
    id: "support",
    routes: [
      {
        path: SUPPORT_PATHS.tickets,
        component: Tickets,
        layout: "app",
        requirement: { authenticated: true, permissions: ["support.ticket.read"] },
        nav: {
          group: "support:group",
          labelKey: "support:nav.tickets",
          section: "main",
          order: 50,
        },
      },
      {
        path: SUPPORT_PATHS.ticket,
        component: OneTicket,
        layout: "app",
        requirement: { authenticated: true, permissions: ["support.ticket.read"] },
      },
    ],
  };
}

export interface PortalSupportOptions {
  /**
   * The company being acted for.
   *
   * Read during render, so it may be a hook. An empty string is valid and
   * means "whichever company the session resolves to" — the server decides,
   * and it refuses a code the account is not linked to.
   */
  clientCode: () => string;
  companyName?: () => string;
}

export function portalSupportRoutes(
  client: ApiClient,
  options: PortalSupportOptions,
): FeatureRoutes {
  function Tickets() {
    const companyName = options.companyName?.();
    return (
      <PortalTicketsScreen
        client={client}
        clientCode={options.clientCode()}
        {...(companyName ? { companyName } : {})}
      />
    );
  }

  function OneTicket() {
    const params: { id?: string } = useParams({ strict: false });
    return (
      <PortalTicketScreen client={client} id={params.id ?? ""} clientCode={options.clientCode()} />
    );
  }

  return {
    id: "support-portal",
    routes: [
      {
        path: SUPPORT_PORTAL_PATHS.tickets,
        component: Tickets,
        layout: "app",
        requirement: { authenticated: true },
        nav: { labelKey: "support:nav.portal", section: "main", order: 16 },
      },
      {
        path: SUPPORT_PORTAL_PATHS.ticket,
        component: OneTicket,
        layout: "app",
        requirement: { authenticated: true },
      },
    ],
  };
}
